import { useMemo, useState } from 'react'
import { CommentComposer } from './CommentComposer'
import { GuideChips } from '../../guides/GuideChips'
import { GuidePicker } from '../../guides/GuidePicker'
import { attachmentUrl } from '../../../services/comments'
import { topicLabelFor } from '../../../utils/topicLabel'
import {
  authorLabel,
  formatWhen,
  isLikedBy,
  isOwnComment,
  isSavedBy,
} from './commentUtils'

// Render a comment's attachments: images as thumbnails, video inline, everything
// else (PDF, docs, any file) as a download/open chip.
function CommentAttachments({ attachments }) {
  if (!attachments || !attachments.length) return null
  return (
    <div className="cmt-attachments">
      {attachments.map((att) => {
        const url = attachmentUrl(att.fileId)
        const type = att.contentType || ''
        if (type.startsWith('image/')) {
          return (
            <a
              key={att.fileId}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="cmt-att-image"
              title={att.name}
            >
              <img src={url} alt={att.name || 'image'} loading="lazy" />
            </a>
          )
        }
        if (type.startsWith('video/')) {
          return (
            <video key={att.fileId} className="cmt-att-video" src={url} controls preload="metadata" />
          )
        }
        return (
          <a
            key={att.fileId}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="cmt-att-file"
            title={att.name}
          >
            <span className="cmt-att-file-icon">{type === 'application/pdf' ? 'PDF' : 'FILE'}</span>
            <span className="cmt-att-file-name">{att.name || 'file'}</span>
          </a>
        )
      })}
    </div>
  )
}

// One chip per topic, labeled with the full ancestor path. The user's topic
// list supplies the path; a topic from someone else uses the names populated
// on the comment.
function TopicTags({ topics, topicOptions }) {
  const labelOf = useMemo(
    () => topicLabelFor([...(topics || []), ...(topicOptions || [])]),
    [topics, topicOptions]
  )
  return (
    <div className="cmt-tags is-topics">
      {topics.map((topic) => {
        const label = labelOf(topic)
        return (
          <span key={topic._id} className="cmt-tag is-topic" title={label}>
            <span className="cmt-tag-label">{label}</span>
          </span>
        )
      })}
    </div>
  )
}

export function CommentCard({
  comment,
  user,
  replies,
  repliesOpen,
  onToggleReplies,
  onLike,
  onSave,
  onShowLikes,
  onOpenItem,
  onUpdate,
  onEditItems,
  onEditTopics,
  onRemove,
  onReply,
  topicOptions = [],
  nested = false,
}) {
  const own = isOwnComment(comment, user?._id)
  const liked = isLikedBy(comment, user?._id)
  const saved = isSavedBy(comment, user?._id)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.text || '')
  const [draftTitle, setDraftTitle] = useState(comment.title || '')
  const [editGuides, setEditGuides] = useState(() => comment.guides || [])
  const [saving, setSaving] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const startEdit = () => {
    setDraft(comment.text || '')
    setDraftTitle(comment.title || '')
    setEditGuides(comment.guides || [])
    setEditing(true)
  }

  const saveEdit = async () => {
    const text = draft.trim()
    if (!text || saving) return
    setSaving(true)
    try {
      await onUpdate(comment._id, {
        title: draftTitle.trim(),
        text,
        guides: editGuides.map((g) => g._id),
      })
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const likeCount = comment.likes?.length || 0
  const anchors = nested ? [] : comment.items || []

  return (
    <article
      id={nested ? undefined : `cmt-${comment._id}`}
      className={'cmt-card' + (nested ? ' is-reply' : '') + (own ? ' is-own' : '')}
    >
      <header className="cmt-card-head">
        <div className="cmt-card-who">
          <span className="cmt-card-name">{authorLabel(comment, user)}</span>
          {comment.author?.occupation ? (
            <span className="cmt-card-meta">{comment.author.occupation}</span>
          ) : null}
        </div>
        <div className="cmt-card-flags">
          {comment.isQuestion ? <span className="cmt-badge">Question</span> : null}
          <span className="cmt-badge is-quiet">{comment.isPublic ? 'Public' : 'Private'}</span>
          {!nested ? (
            <div className="cmt-menu">
              <button
                type="button"
                className="cmt-menu-btn"
                aria-label="Comment options"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 120)}
              >
                ⋯
              </button>
              {menuOpen ? (
                <div className="cmt-menu-pop" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onMouseDown={() => onSave?.(comment)}
                  >
                    {saved ? 'Unbookmark' : 'Bookmark'}
                  </button>
                  {own ? (
                    <button
                      type="button"
                      role="menuitem"
                      onMouseDown={() => onEditItems?.(comment)}
                    >
                      Edit items
                    </button>
                  ) : null}
                  {own ? (
                    <button
                      type="button"
                      role="menuitem"
                      onMouseDown={() => onEditTopics?.(comment)}
                    >
                      Edit topics
                    </button>
                  ) : null}
                  {own ? (
                    <button
                      type="button"
                      role="menuitem"
                      onMouseDown={startEdit}
                    >
                      Edit text
                    </button>
                  ) : null}
                  {own ? (
                    <button
                      type="button"
                      role="menuitem"
                      onMouseDown={() => onUpdate?.(comment._id, { isPublic: !comment.isPublic })}
                    >
                      {comment.isPublic ? 'Make private' : 'Make public'}
                    </button>
                  ) : null}
                  {own ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="is-danger"
                      onMouseDown={() => onRemove?.(comment)}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {anchors.length ? (
        <div className="cmt-tags">
          {anchors.map((a) => (
            <button
              key={a.item || `${a.standard}-${a.order}`}
              type="button"
              className="cmt-tag is-open"
              title={`${a.label}${a.pageStart != null ? ` · p.${a.pageStart}` : ''} — open in viewer`}
              onClick={() => onOpenItem?.(a)}
            >
              <span className="cmt-tag-label">{a.label}</span>
              {a.pageStart != null ? <span className="cmt-tag-page">· p.{a.pageStart}</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      {!nested && comment.topics?.length ? (
        <TopicTags topics={comment.topics} topicOptions={topicOptions} />
      ) : null}

      {editing ? (
        <div className="cmt-edit">
          <input
            className="cmt-composer-title"
            type="text"
            value={draftTitle}
            placeholder="Title (optional)"
            disabled={saving}
            onChange={(e) => setDraftTitle(e.target.value)}
          />
          <textarea
            className="cmt-composer-input"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
          />
          <div className="cmt-compose-guides">
            <GuidePicker value={editGuides} onChange={setEditGuides} />
          </div>
          <div className="cmt-composer-row">
            <button type="button" className="cmt-chip" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="cmt-post" onClick={saveEdit} disabled={saving || !draft.trim()}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          {comment.title?.trim() ? <p className="cmt-card-title">{comment.title}</p> : null}
          <p className="cmt-card-body">{comment.text}</p>
        </>
      )}

      <CommentAttachments attachments={comment.attachments} />
      <GuideChips guides={comment.guides} />

      <footer className="cmt-card-foot">
        <button type="button" className={'cmt-action' + (liked ? ' is-on' : '')} onClick={() => onLike(comment)}>
          Like
        </button>
        {likeCount ? (
          <button type="button" className="cmt-action is-quiet" onClick={() => onShowLikes?.(comment)}>
            {likeCount === 1 ? '1 like' : `${likeCount} likes`}
          </button>
        ) : null}
        {!nested ? (
          <button type="button" className="cmt-action" onClick={() => onToggleReplies(comment._id)}>
            {comment.replyCount === 1 ? '1 reply' : `${comment.replyCount || 0} replies`}
          </button>
        ) : null}
        {nested && own ? (
          <>
            <button
              type="button"
              className="cmt-action"
              onClick={() => {
                setDraft(comment.text || '')
                setEditing(true)
              }}
            >
              Edit
            </button>
            <button type="button" className="cmt-action is-danger" onClick={() => onRemove(comment)}>
              Delete
            </button>
          </>
        ) : null}
        <span className="cmt-when">{formatWhen(comment.createdAt)}</span>
      </footer>

      {repliesOpen ? (
        <div className="cmt-replies">
          {(replies || []).map((reply) => (
            <CommentCard
              key={reply._id}
              comment={reply}
              user={user}
              nested
              onLike={onLike}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
          <CommentComposer
            compact
            placeholder="Write a reply…"
            submitLabel="Reply"
            onSubmit={(payload) => onReply(comment._id, payload)}
          />
        </div>
      ) : null}
    </article>
  )
}
